import * as React from "react";
import { LoaderFunctionArgs, useLoaderData } from "react-router";
import axios, { AxiosRequestConfig, CancelTokenSource } from "axios";
import { LoaderData } from "../account/MyAccount";

import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import { visuallyHidden } from "@mui/utils";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { Button } from "@mui/material";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import { TLCEventManager } from "../../../events/EventManager";
import { Link } from "react-router-dom";
import { getSearchURL } from "../title/SearchLink";
import { useDialog } from "@tlc/ui/dialog/useDialog";

export interface TagCount {
  tag: string;
  count: number;
}

export interface ListCountReturn {
  count: number;
}

export interface TagList {
  tags: string[];
}

// TODO move these Tag API calls to a separate service, holder, or context?
export async function getTagCounts(
  searchString: string = "",
  page: number = 0,
  maxResults: number = 10,
  source: CancelTokenSource = null,
) {
  try {
    const config: AxiosRequestConfig = {
      params: {
        searchString,
        firstResult: page * maxResults,
        maxResults,
      },
    };
    if (source) {
      config.cancelToken = source.token;
    }
    const { data } = await axios.get<TagCount[]>(`/tag/allTags`, config);
    return data;
  } catch (error) {
    console.error("Error fetching tag counts", error);
    throw error;
  }
}

export async function getUnblockedTags(
  searchString: string = "",
  page: number = 0,
  maxResults: number = 10,
  source: CancelTokenSource = null,
) {
  try {
    const config: AxiosRequestConfig = {
      params: {
        searchString,
        firstResult: page * maxResults,
        maxResults,
      },
    };
    if (source) {
      config.cancelToken = source.token;
    }
    const { data } = await axios.get<string[]>(`/tag/unBlockedTags`, config);
    return data;
  } catch (error) {
    console.error("Error fetching tag counts", error);
    throw error;
  }
}

async function removeTag(tags: string[]): Promise<number> {
  try {
    const tagList: TagList = { tags };
    const response = await axios.post<number>("/tag/remove", tagList);
    return response.data;
  } catch (error) {
    console.error("Error removing tags", error);
    throw error;
  }
}

export async function fetchAllTags() {
  const totalTags = await getTotalDistinctTags();
  const tagCounts = await getTagCounts("", 0, totalTags);
  return tagCounts;
}

export async function fetchAllUnblockedTags() {
  const totalTags = await getTotalDistinctTags();
  const tagCounts = await getUnblockedTags("", 0, totalTags);
  return tagCounts;
}

export async function getTotalDistinctTags(
  searchString: string = "",
  source: CancelTokenSource = null,
) {
  try {
    const config: AxiosRequestConfig = {
      params: {
        searchString,
      },
    };
    if (source) {
      config.cancelToken = source.token;
    }
    const { data } = await axios.get<ListCountReturn>(
      `/tag/allTags/count`,
      config,
    );
    return data.count;
  } catch (error) {
    console.error("Error fetching total distinct tags", error);
    throw error;
  }
}

export async function tagsLoader({ request }: LoaderFunctionArgs) {
  console.log("tagsLoader executing");

  const tagCounts = await getTagCounts();
  const total = await getTotalDistinctTags();

  return {
    tagCounts,
    total,
  };
}

export const TagManagementPage: React.FC = () => {
  const cancelTokenRef = React.useRef<CancelTokenSource>(null); // avoids reinitializing on every render
  const searchFilterTimeoutIdRef = React.useRef<number>(null); // avoids reinitializing on every render

  const { tagCounts, total: initialTotal } = useLoaderData() as LoaderData<
    typeof tagsLoader
  >;
  const { translate } = useLanguage();
  const [selected, setSelected] = React.useState<TagCount[]>([]);
  const selectedTags = React.useMemo<string[]>(
    () => selected.map((tagCount) => tagCount.tag),
    [selected],
  );
  const [page, setPage] = React.useState<number>(0);
  const [filtered, setFiltered] = React.useState<boolean>(false);
  const [total, setTotal] = React.useState<number>(initialTotal);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const { open: confirmDeleteOpen, closeDialog, openDialog } = useDialog();
  const [displayedRows, setDisplayedRows] =
    React.useState<TagCount[]>(tagCounts);

  const emptyRows = React.useMemo<number>(() => {
    return pageSize - displayedRows.length;
  }, [displayedRows, pageSize]);

  React.useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
      if (searchFilterTimeoutIdRef.current) {
        clearTimeout(searchFilterTimeoutIdRef.current);
      }
    };
  }, []);

  const isSelected = (id: string) => selectedTags.indexOf(id) !== -1;
  const handleChangePage = async (event: unknown, newPage: number) => {
    setPage(newPage);
    const tagCounts = await getTagCounts(searchTerm, newPage, pageSize);
    setDisplayedRows(tagCounts);
  };
  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(size);
    setPage(0);
    const tagCounts = await getTagCounts(searchTerm, 0, size);
    setDisplayedRows(tagCounts);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    tagCount: TagCount,
  ) => {
    const { tag } = tagCount;
    const selectedIndex = selectedTags.indexOf(tag);

    if (selectedIndex === -1) {
      setSelected([...selected, tagCount]);
    } else {
      setSelected(selected.filter((tc) => tc.tag !== tag));
    }
  };

  const handleSubmitDelete = async (event: React.MouseEvent) => {
    setDeleting(true);
    const removedCount = await removeTag([].concat(selectedTags));
    if (removedCount > 0) {
      SuccessMessageControl.show(
        translate("tagsRemovedMessage", {
          MATERIALCOUNT: removedCount.toString(),
        }),
      );

      // refetch
      const tagCounts = await getTagCounts(searchTerm, 0, pageSize);
      const total = await getTotalDistinctTags(searchTerm);
      setTotal(total);
      setDisplayedRows(tagCounts);
      closeDialog();
      setTimeout(() => setSelected([])); // until the dialog is closed, we don't want to modify selected
    }
    setDeleting(false);
  };

  const handleFilter = async (term: string) => {
    // Cancel the previous request if it exists
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled by the user.");
    }

    // Create a new cancel token
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      setPage(0); // reset the page index when filtering
      const tagCounts = await getTagCounts(
        term,
        0,
        pageSize,
        cancelTokenRef.current,
      );
      const total = await getTotalDistinctTags(term, cancelTokenRef.current);
      setFiltered(term.length > 0);
      setTotal(total);
      setDisplayedRows(tagCounts);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        throw error;
      }
    }
  };

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (searchFilterTimeoutIdRef.current) {
      clearTimeout(searchFilterTimeoutIdRef.current);
      searchFilterTimeoutIdRef.current = null;
    }
    const search = event.target.value;
    setSearchTerm(search);

    searchFilterTimeoutIdRef.current = window.setTimeout(() => {
      handleFilter(search);
    }, 250);
  };

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      const allTags = await getTagCounts(searchTerm, 0, total);
      setSelected(allTags);
      return;
    }
    setSelected([]);
  };

  const getTotalMaterialCount = (tagCounts: TagCount[]): number => {
    return tagCounts.reduce((total, tagCount) => total + tagCount.count, 0);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Toolbar
            sx={{
              flexWrap: "wrap",
              gap: 1,
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              ...(selected.length > 0 && {
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity,
                  ),
              }),
            }}
          >
            {selected.length > 0 ? (
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selected.length} {translate("selected")}
              </Typography>
            ) : (
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                variant="h6"
                id="tableTitle"
                component="div"
              >
                {total}{" "}
                {filtered ? translate("matchingTags") : translate("tags")}
              </Typography>
            )}
            {selected.length === 0 && (
              <TextField
                sx={{ minWidth: "232px", flexShrink: 0 }}
                placeholder={translate("searchTags")}
                hiddenLabel={true}
                onChange={handleSearchTermChange}
                value={searchTerm}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    "aria-label": translate("searchTags"),
                  },
                }}
              />
            )}
            {selected.length > 0 && (
              <Tooltip title={translate("removeSelected")}>
                <IconButton onClick={openDialog}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
          <Table aria-labelledby="tableTitle" size={"medium"}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < total
                    }
                    checked={total > 0 && selected.length === total}
                    onChange={handleSelectAllClick}
                    slotProps={{
                      input: {
                        "aria-label": translate("selectAll"),
                      },
                    }}
                  />
                  <Box component="span" sx={visuallyHidden}>
                    {translate("selectAll")}
                  </Box>
                </TableCell>
                <TableCell key={"tag"} align={"left"} padding={"normal"}>
                  {translate("advancedSearch_tag")}
                </TableCell>
                <TableCell key={"total"} align={"right"} padding={"normal"}>
                  {translate("total")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((row, index) => {
                const isItemSelected = isSelected(row.tag);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row)}
                    key={row.tag}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick(e, row);
                        }}
                        slotProps={{
                          input: {
                            "aria-labelledby": labelId,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell id={labelId} scope="row" align={"left"}>
                      <Link
                        to={getSearchURL(row.tag, "tag")}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: "block",
                          maxWidth: "75ch",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.tag}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                );
              })}
              {/* prevent layout jump */}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={3} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          slotProps={{
            select: {
              inputProps: { "aria-label": translate("rowsPerPage") },
              native: true,
            },
          }}
        />
      </Paper>
      {/* TODO this Dialog should probably be a subcomponent */}
      <Dialog open={confirmDeleteOpen} onClose={closeDialog}>
        <DialogContent>
          <DialogTitle>{translate("areYouSureTitle")}</DialogTitle>
          <DialogContentText>
            {selected.length > 1 && (
              <>
                {translate("areYouSureRemoveTags", {
                  TAGCOUNT: selected.length.toString(),
                  MATERIALCOUNT: getTotalMaterialCount(selected).toString(),
                })}
              </>
            )}
            {selected.length === 1 && (
              <>
                {translate("areYouSureRemoveTag", {
                  MATERIALCOUNT: getTotalMaterialCount(selected).toString(),
                })}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} autoFocus onClick={closeDialog}>
            {translate("cancel")}
          </Button>
          <Button
            disabled={deleting}
            color="error"
            variant="outlined"
            startIcon={
              deleting ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <DeleteIcon />
              )
            }
            onClick={handleSubmitDelete}
          >
            {translate("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
